import { CostPriceAnalysis, CostPriceIngredient, EnrichedCostPriceAnalysis, ByoCostBreakdown, createCostPriceAnalysis } from '../services/costPrice/costPriceService';
import { OrderHistory, EmailProductData } from '../stores/appOrderHistoryStore';
import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { CustomIngredient } from '../services/customIngredients/customIngredientsService';
import { Subrecipe, SubrecipeIngredient } from '../services/subrecipes/subrecipesService';
import { resolveProductIdentifier, isValuationCandidate, selectValuationLine, resolveValuationQuantityInfo } from './platformUtils';
import { getItemTotalRevenue } from './revenueAddOns';
import { applyWaste, resolveWaste } from './wasteUtils';

export const SUBRECIPE_MAX_DEPTH = 3;

// BYO = build-your-own. Value used on `CostPriceAnalysis.costPriceType` to mark
// a row as a standalone, shared choice cost (one per restaurant + choiceName).
// CHOICE rows are excluded from category tabs / KPIs and only show under the
// Add-ons tab on CostPricePageNew.
export const COST_PRICE_TYPE_CHOICE = 'CHOICE';

/**
 * Stable identity for an ingredient row inside a CHOICE analysis, used to key
 * per-dish quantity overrides (`byoChoiceOverrides`). Row ids are regenerated
 * by the server on every update, so identity comes from content instead.
 * Mirrors the server helper of the same name in dishCostCalc.ts.
 */
export function choiceIngredientKey(ing: CostPriceIngredient): string {
  if (ing.subrecipeId && !ing.articleId) return `sub:${ing.subrecipeId}`;
  if (ing.nestedCostPriceId) return `nested:${ing.nestedCostPriceId}`;
  return `${ing.platform}:${ing.articleId}`;
}

/**
 * Apply a dish's quantity overrides to a CHOICE's ingredient rows. The
 * override value replaces whichever quantity field the row uses
 * (quantityNeeded / subrecipePortionsNeeded / nestedQuantityNeeded). Keys that
 * match no row are ignored; rows without an override pass through unchanged.
 * Mirrors the server helper of the same name in dishCostCalc.ts.
 */
export function applyByoQuantityOverrides(
  ingredients: CostPriceIngredient[],
  overrides: Record<string, number> | undefined
): CostPriceIngredient[] {
  if (!overrides) return ingredients;
  return ingredients.map(ing => {
    const qty = overrides[choiceIngredientKey(ing)];
    if (qty === undefined || !Number.isFinite(qty) || qty <= 0) return ing;
    if (ing.subrecipeId && !ing.articleId) return { ...ing, subrecipePortionsNeeded: qty };
    if (ing.nestedCostPriceId) return { ...ing, nestedQuantityNeeded: qty };
    return { ...ing, quantityNeeded: qty };
  });
}

/**
 * Find an existing CHOICE-typed cost price for a given restaurant + choice name,
 * or create one if it doesn't exist yet. Lazy creation is the spec's
 * decision 7 — a CHOICE row only materialises the first time a user clicks
 * that choice inside a BYO dish modal.
 *
 * On create, `categoryId` is inherited from the parent BYO dish (decision 8) —
 * sensible default for the Add-ons tab listing; the user can change it later
 * in the choice editor.
 */
export async function findOrCreateChoiceCostPrice(
  restaurantId: string,
  choiceName: string,
  parentCategoryId: string | undefined,
  allCostPriceAnalyses: CostPriceAnalysis[],
): Promise<CostPriceAnalysis> {
  const existing = allCostPriceAnalyses.find(
    a => a.costPriceType === COST_PRICE_TYPE_CHOICE
      && a.restaurantId === restaurantId
      && a.costPriceName === choiceName,
  );
  if (existing) return existing;

  return createCostPriceAnalysis({
    restaurantId,
    costPriceName: choiceName,
    categoryId: parentCategoryId,
    ingredients: [],
    costPriceType: COST_PRICE_TYPE_CHOICE,
  });
}

/**
 * Count how many BYO dishes (other cost-price analyses with a `menuItemName`)
 * link to revenue items that contain the given choice in the trailing window.
 * Used in the choice editor as "Used in N dishes" so the user understands the
 * edit propagates.
 */
export function countBYODishesUsingChoice(
  choiceName: string,
  restaurantId: string,
  allCostPriceAnalyses: CostPriceAnalysis[],
  revenueHistory: RevenueHistory[],
): number {
  let count = 0;
  for (const analysis of allCostPriceAnalyses) {
    if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) continue;
    if (analysis.restaurantId !== restaurantId) continue;
    if (!analysis.menuItemName) continue;
    const aggs = getByoChoiceAggregates(analysis.menuItemName, revenueHistory);
    if (aggs?.choices.has(choiceName)) count++;
  }
  return count;
}

/**
 * Per-order-history-array index: `${platform}\u0000${productIdentifier}` -> the orders containing that
 * product at a usable price, newest first. Built once per array (keyed on the array identity, so each
 * period's `ordersUpToPeriod` slice gets its own) and reused by every ingredient lookup, replacing a
 * full filter+sort of the order history per ingredient.
 *
 * €0 lines are never indexed: a one-off free bottle must not become an ingredient's current price.
 * Credit lines ARE indexed — `selectValuationLine` only falls back to them for products that were
 * never charged for at all. Mirrors the server's `findLatestProductForIngredient` in `dishCostCalc.ts`.
 *
 * Ordering is reproduced exactly as before: buckets are filled in the array's own order and then
 * stable-sorted by orderDate descending — identical to the old `filter(...).sort(desc)`, including how
 * orders sharing an orderDate tie-break.
 */
type OrderProductIndex = Map<string, OrderHistory[]>;
const orderProductIndexCache = new WeakMap<OrderHistory[], OrderProductIndex>();

function getOrderProductIndex(orderHistory: OrderHistory[]): OrderProductIndex {
  const cached = orderProductIndexCache.get(orderHistory);
  if (cached) return cached;

  const index: OrderProductIndex = new Map();
  for (const order of orderHistory) {
    const seenInOrder = new Set<string>();
    for (const product of order.productData || []) {
      const identifier = resolveProductIdentifier(order.platform, product.articleId, product.name);
      // One entry per order per product identity — the old code used `.some()`, so an order with two
      // lines of the same product still counted once. €0 lines never enter the index at all: they can
      // never price anything, and letting one in would hide the order's real paid line.
      if (!identifier || !isValuationCandidate(product) || seenInOrder.has(identifier)) continue;
      seenInOrder.add(identifier);
      const key = `${order.platform}\u0000${identifier}`;
      const bucket = index.get(key);
      if (bucket) bucket.push(order);
      else index.set(key, [order]);
    }
  }
  index.forEach(bucket => {
    bucket.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
  });

  orderProductIndexCache.set(orderHistory, index);
  return index;
}

/**
 * Calculates current price for an ingredient based on order history data or custom ingredient data
 */
export function calculateCurrentPrice(
  ingredient: CostPriceIngredient,
  orderHistory: OrderHistory[],
  customIngredients?: CustomIngredient[],
  showExVat: boolean = true
): { currentPrice?: number; currentPriceDate?: Date } {
  // If it's a custom ingredient, use the stored price directly
  if (ingredient.custom && customIngredients) {
    const customIngredient = customIngredients.find(custom => 
      custom.articleId === ingredient.articleId && 
      custom.platform === ingredient.platform
    );
    
    if (customIngredient) {
      // For custom ingredients, price is already per stdAmount, so calculate price per unit.
      // Basis follows the VAT toggle, exactly like order products below.
      // Use Math.abs to ensure cost price always uses positive values (credit notes can have negative prices)
      const base = showExVat ? customIngredient.price : (customIngredient.priceWithTaxes ?? customIngredient.price);
      const pricePerStdUnit = Math.abs(base / customIngredient.quantityInfo.stdAmount);

      return {
        currentPrice: pricePerStdUnit,
        currentPriceDate: new Date(customIngredient.updatedAt)
      };
    }
  }
  
  // For regular ingredients, find the most recent order containing this product. Resolve BOTH sides
  // through resolveProductIdentifier so the stored ingredient and the order line normalize identically
  // (handles 'no_article_id' platforms, LLM name noise, and vhcjongens' shared article ids).
  const ingredientIdentifier = resolveProductIdentifier(
    ingredient.platform || '', ingredient.articleId, ingredient.productName
  );
  if (!ingredientIdentifier) {
    return { currentPrice: undefined, currentPriceDate: undefined };
  }
  const matchesIngredient = (product: { articleId?: string; name?: string }, platform: string) =>
    resolveProductIdentifier(platform, product.articleId, product.name) === ingredientIdentifier;

  const relevantOrders = getOrderProductIndex(orderHistory).get(`${ingredient.platform}\u0000${ingredientIdentifier}`);

  if (!relevantOrders || relevantOrders.length === 0) {
    return { currentPrice: undefined, currentPriceDate: undefined };
  }

  // Every matching line newest-first (one invoice can carry "5 × €72.55" AND "1 × gratis"), then the
  // shared tiered rule picks which one prices the ingredient. Server twin: findLatestProductForIngredient.
  const candidates: Array<{ order: OrderHistory; product: EmailProductData }> = [];
  for (const order of relevantOrders) {
    for (const p of order.productData) {
      if (matchesIngredient(p, order.platform)) candidates.push({ order, product: p });
    }
  }
  const chosen = selectValuationLine(candidates, c => c.product.price);
  const product = chosen?.product;

  if (!product) {
    return { currentPrice: undefined, currentPriceDate: undefined };
  }

  // Mis-parse shield: divide by the line's own quantityInfo only when its stdUnit agrees
  // with the ingredient's stored snapshot; on contradiction (or missing line info) the
  // snapshot is the divisor. Server twin: dishCostCalc.findLatestIngredientPrice.
  const valuationInfo = resolveValuationQuantityInfo(ingredient.quantityInfo, product.quantityInfo);
  if (!valuationInfo) {
    return { currentPrice: undefined, currentPriceDate: undefined };
  }

  // Get the appropriate price based on VAT toggle
  const productPrice = showExVat ? product.price : (product.priceWithTaxes ?? product.price);

  if (!productPrice) {
    return { currentPrice: undefined, currentPriceDate: undefined };
  }

  // Calculate proportional price based on quantity needed vs product quantity
  // Use Math.abs to ensure cost price always uses positive values (credit notes can have negative prices)
  const productQuantityInStdUnits = valuationInfo.stdAmount * valuationInfo.stdMultiplier;
  const pricePerStdUnit = Math.abs(productPrice / productQuantityInStdUnits);
  
  return {
    currentPrice: pricePerStdUnit,
    currentPriceDate: new Date(chosen.order.orderDate)
  };
}

/**
 * Total live cost for a single portion of a subrecipe, expanding nested subrecipes
 * recursively. Returns 0 (and console.warn) for orphans, cycles, or when a referenced
 * subrecipe has portions = 0.
 *
 * Independent guards from the nested-cost-price recursion: separate depth counter
 * (`_subrecipeDepth`) and visited set (`_visitedSubrecipeIds`). A cost-price tree
 * at its max depth can still expand a subrecipe tree.
 *
 * Returns: cost per 1 "portion unit" of this subrecipe (i.e. subrecipe total cost
 * divided by subrecipe.portions). Callers multiply by their portionsNeeded.
 */
export function calculateSubrecipeUnitCost(
  subrecipe: Subrecipe,
  orderHistory: OrderHistory[],
  customIngredients: CustomIngredient[] | undefined,
  showExVat: boolean,
  allSubrecipes: Map<string, Subrecipe>,
  defaultWastePercentage: number = 0,
  _subrecipeDepth: number = 0,
  _visitedSubrecipeIds: Set<string> = new Set()
): number {
  if (_visitedSubrecipeIds.has(subrecipe.id)) {
    console.warn(`Circular subrecipe reference detected at ${subrecipe.id} (${subrecipe.name})`);
    return 0;
  }
  if (_subrecipeDepth >= SUBRECIPE_MAX_DEPTH) {
    console.warn(`Subrecipe recursion exceeded ${SUBRECIPE_MAX_DEPTH} levels at ${subrecipe.id} (${subrecipe.name})`);
    return 0;
  }
  if (!subrecipe.portions || subrecipe.portions === 0) {
    console.warn(`Subrecipe ${subrecipe.id} (${subrecipe.name}) has portions = 0; treating cost as 0`);
    return 0;
  }

  const nextVisited = new Set(_visitedSubrecipeIds);
  nextVisited.add(subrecipe.id);

  // Total cost of producing the subrecipe's full yield (subrecipe.portions worth).
  const totalCostForFullYield = subrecipe.ingredients.reduce((sum, ing) => {
    // Nested subrecipe row inside this subrecipe
    if (ing.nestedSubrecipeId) {
      const childSubrecipe = allSubrecipes.get(ing.nestedSubrecipeId);
      if (!childSubrecipe) {
        console.warn(`Orphan nested subrecipe ${ing.nestedSubrecipeId} inside ${subrecipe.id}`);
        return sum;
      }
      const childUnitCost = calculateSubrecipeUnitCost(
        childSubrecipe,
        orderHistory,
        customIngredients,
        showExVat,
        allSubrecipes,
        defaultWastePercentage,
        _subrecipeDepth + 1,
        nextVisited
      );
      return sum + childUnitCost * (ing.nestedSubrecipePortionsNeeded ?? 0);
    }

    // Normal ingredient row
    return sum + ingredientCostFromSubrecipeRow(ing, orderHistory, customIngredients, showExVat, defaultWastePercentage);
  }, 0);

  return totalCostForFullYield / subrecipe.portions;
}

/**
 * Total cost for one full yield of a subrecipe (= unitCost * portions).
 * Recursively expands nested subrecipes; safe against orphans, cycles, and
 * subrecipe.portions = 0 (returns 0 in those cases).
 */
export function calculateSubrecipeTotalCost(
  subrecipe: Subrecipe,
  orderHistory: OrderHistory[],
  customIngredients: CustomIngredient[] | undefined,
  showExVat: boolean,
  allSubrecipes: Map<string, Subrecipe>,
  defaultWastePercentage: number = 0
): number {
  const unitCost = calculateSubrecipeUnitCost(
    subrecipe,
    orderHistory,
    customIngredients,
    showExVat,
    allSubrecipes,
    defaultWastePercentage
  );
  return unitCost * subrecipe.portions;
}

/**
 * Cost contribution of a single normal-ingredient row inside a subrecipe.
 * Mirrors the per-unit math used by the cost-price-side calc.
 */
function ingredientCostFromSubrecipeRow(
  ing: SubrecipeIngredient,
  orderHistory: OrderHistory[],
  customIngredients: CustomIngredient[] | undefined,
  showExVat: boolean,
  defaultWastePercentage: number = 0
): number {
  if (!ing.articleId || ing.quantityNeeded === undefined || !ing.platform) return 0;
  // Reuse the cost-price-side price lookup by adapting the shape.
  const ingredientAsCostPriceShape: CostPriceIngredient = {
    id: ing.id,
    articleId: ing.articleId,
    productName: ing.productName ?? '',
    quantityNeeded: ing.quantityNeeded,
    quantityInfo: ing.quantityInfo ?? { stdAmount: 0, stdMultiplier: 0, stdUnit: '' },
    platform: ing.platform,
    customPlatformName: ing.customPlatformName,
    custom: ing.isCustom,
  };
  const { currentPrice } = calculateCurrentPrice(ingredientAsCostPriceShape, orderHistory, customIngredients, showExVat);
  if (!currentPrice) return 0;
  const wastedQty = applyWaste(ing.quantityNeeded, resolveWaste(ing.wastePercentage, defaultWastePercentage));
  return wastedQty * currentPrice;
}

/** Cost of an already-enriched row, shared by the dish total and review display. */
export function calculateIngredientContribution(ingredient: CostPriceIngredient, defaultWastePercentage = 0): number {
  if (ingredient.subrecipeId && ingredient.currentPrice !== undefined) {
    return ingredient.currentPrice * (ingredient.subrecipePortionsNeeded ?? 0);
  }
  if (ingredient.nestedCostPriceId && ingredient.currentPrice) {
    return ingredient.currentPrice * (ingredient.nestedQuantityNeeded || 1);
  }
  if (ingredient.currentPrice && ingredient.quantityNeeded !== undefined) {
    return applyWaste(ingredient.quantityNeeded, resolveWaste(ingredient.wastePercentage, defaultWastePercentage)) * ingredient.currentPrice;
  }
  return 0;
}

/**
 * Enriches cost price analysis with current pricing data from order history and custom ingredients.
 * Supports nested cost price items AND nested subrecipes (recursive expansion with separate
 * max-depth budgets).
 *
 * BYO (build-your-own): when `revenueHistory` is provided and the dish's
 * `menuItemName` resolves to revenue items with choices in the trailing 2-month
 * window, this function additionally:
 *   - resolves each choice name to a CHOICE-type `CostPriceAnalysis` in
 *     `allCostPriceAnalyses` (find by `(restaurantId, costPriceName)`),
 *     recursively enriching it like a nested cost price;
 *   - computes the weighted dish cost / weighted menu price per the B2 formulas;
 *   - overrides `totalCost` with the weighted average per dish, and attaches a
 *     `byo` breakdown.
 *
 * Non-BYO analyses are completely unaffected — `byo` stays undefined.
 */
/**
 * Optional inputs for cost-price enrichment. Grouped into one object so call
 * sites read by name instead of threading a long positional list.
 */
export interface EnrichmentOptions {
  customIngredients?: CustomIngredient[];
  showExVat?: boolean; // default true
  allCostPriceAnalyses?: CostPriceAnalysis[];
  allSubrecipes?: Map<string, Subrecipe>;
  revenueHistory?: RevenueHistory[];
  // Cross-restaurant sourcing: per-restaurant input data for ingredients that
  // carry a `sourceRestaurantId`. When omitted, behaviour is byte-identical to
  // single-restaurant — the positional `orderHistory` / `customIngredients` /
  // `allSubrecipes` are treated as the data for `currentRestaurantId`.
  byRestaurant?: {
    orders: Map<string, OrderHistory[]>;
    custom: Map<string, CustomIngredient[]>;
    subrecipes: Map<string, Map<string, Subrecipe>>;
  };
  currentRestaurantId?: string;
  // Restaurant-wide default waste %, the inherit fallback for ingredients with
  // no explicit `wastePercentage`. Omitted/0 → cost is byte-identical to before.
  defaultWastePercentage?: number;
}

export function enrichCostPriceWithCurrentPrices(
  costPriceAnalysis: CostPriceAnalysis,
  orderHistory: OrderHistory[],
  opts: EnrichmentOptions = {}
): EnrichedCostPriceAnalysis {
  return enrichCostPriceRecursive(costPriceAnalysis, orderHistory, opts, 0, new Set());
}

/**
 * Collect the distinct `sourceRestaurantId`s referenced by a set of analyses'
 * top-level ingredient rows (order-history, custom, AND subrecipe-reference),
 * excluding the current restaurant. Used to know which siblings' data sets must
 * be loaded before enriching. Returns [] when nothing is cross-restaurant.
 */
export function collectSourceRestaurantIds(
  analyses: CostPriceAnalysis[],
  currentRestaurantId?: string
): string[] {
  const ids = new Set<string>();
  for (const analysis of analyses) {
    for (const ing of analysis.ingredients) {
      if (ing.sourceRestaurantId && ing.sourceRestaurantId !== currentRestaurantId) {
        ids.add(ing.sourceRestaurantId);
      }
    }
  }
  return Array.from(ids);
}

// Internal recursive worker — carries the recursion state (`_depth`,
// `_visitedIds`) that callers never need to see.
function enrichCostPriceRecursive(
  costPriceAnalysis: CostPriceAnalysis,
  orderHistory: OrderHistory[],
  opts: EnrichmentOptions,
  _depth: number,
  _visitedIds: Set<string>
): EnrichedCostPriceAnalysis {
  const { customIngredients, showExVat = true, allCostPriceAnalyses, allSubrecipes, revenueHistory, byRestaurant, currentRestaurantId, defaultWastePercentage } = opts;
  const MAX_DEPTH = 3; // Per-tree budget for nested cost prices

  // Resolve the orders / custom / subrecipes data set an ingredient should be
  // priced against. A sibling-sourced ingredient (sourceRestaurantId differs from
  // the current restaurant) draws from `byRestaurant`; everything else uses the
  // positional single-restaurant data (current restaurant). Missing sibling data
  // degrades gracefully to empty sets → price undefined → contributes 0.
  const resolveData = (sourceRestaurantId?: string): {
    orders: OrderHistory[];
    custom?: CustomIngredient[];
    subrecipes?: Map<string, Subrecipe>;
  } => {
    if (sourceRestaurantId && byRestaurant && sourceRestaurantId !== currentRestaurantId) {
      return {
        orders: byRestaurant.orders.get(sourceRestaurantId) ?? [],
        custom: byRestaurant.custom.get(sourceRestaurantId) ?? [],
        subrecipes: byRestaurant.subrecipes.get(sourceRestaurantId) ?? new Map<string, Subrecipe>(),
      };
    }
    return { orders: orderHistory, custom: customIngredients, subrecipes: allSubrecipes };
  };

  // Check for circular references
  if (_visitedIds.has(costPriceAnalysis.id)) {
    console.warn(`Circular reference detected for cost price analysis ${costPriceAnalysis.id}`);
    return {
      ...costPriceAnalysis,
      totalCost: 0
    };
  }

  // Add current ID to visited set
  const newVisitedIds = new Set(_visitedIds);
  newVisitedIds.add(costPriceAnalysis.id);

  const enrichedIngredients = costPriceAnalysis.ingredients.map(ingredient => {
    // Subrecipe reference (the new shape after migration): resolve live from store.
    // A sibling-sourced subrecipe is costed ENTIRELY in its source restaurant's
    // context (its orders/custom + its own subrecipe map for nested subrecipes).
    const subrecipeData = resolveData(ingredient.sourceRestaurantId);
    if (ingredient.subrecipeId && subrecipeData.subrecipes) {
      const subrecipe = subrecipeData.subrecipes.get(ingredient.subrecipeId);
      if (subrecipe) {
        const unitCost = calculateSubrecipeUnitCost(
          subrecipe,
          subrecipeData.orders,
          subrecipeData.custom,
          showExVat,
          subrecipeData.subrecipes,
          defaultWastePercentage ?? 0,
          0,
          new Set()
        );
        return {
          ...ingredient,
          currentPrice: Math.abs(unitCost),
          currentPriceDate: new Date(subrecipe.updatedAt),
        };
      }
      // Orphan reference — leave currentPrice undefined; UI shows "no longer available".
      console.warn(`Orphan subrecipe reference ${ingredient.subrecipeId} in cost price ${costPriceAnalysis.id}`);
      return { ...ingredient, currentPrice: undefined, currentPriceDate: undefined };
    }

    // Check if this is a nested cost price item
    if (ingredient.nestedCostPriceId && allCostPriceAnalyses && _depth < MAX_DEPTH) {
      // Find the nested cost price analysis
      const nestedAnalysis = allCostPriceAnalyses.find(a => a.id === ingredient.nestedCostPriceId);

      if (nestedAnalysis) {
        // Recursively enrich the nested analysis
        const enrichedNestedAnalysis = enrichCostPriceRecursive(
          nestedAnalysis,
          orderHistory,
          opts,
          _depth + 1,
          newVisitedIds
        );

        return {
          ...ingredient,
          currentPrice: Math.abs(enrichedNestedAnalysis.totalCost), // Store the unit price (per 1 nested item), always positive
          currentPriceDate: new Date(enrichedNestedAnalysis.updatedAt)
        };
      }
    }

    // Regular ingredient (order-history product or custom) - price from the
    // ingredient's source restaurant data when cross-restaurant, else current.
    const { orders: resolvedOrders, custom: resolvedCustom } = resolveData(ingredient.sourceRestaurantId);
    const { currentPrice, currentPriceDate } = calculateCurrentPrice(ingredient, resolvedOrders, resolvedCustom, showExVat);
    return {
      ...ingredient,
      currentPrice,
      currentPriceDate
    };
  });

  // Calculate base total cost from this dish's own ingredients (no choices yet).
  const baseCost = enrichedIngredients.reduce((sum, ingredient) => sum + calculateIngredientContribution(ingredient, defaultWastePercentage), 0);

  // BYO = build-your-own. Only compute the weighted breakdown when revenue is
  // supplied (call sites in modern cost-price pages pass it; legacy callers
  // omit it and get the non-BYO behaviour unchanged). CHOICE rows themselves
  // never become BYO — they have no menuItemName.
  let byo: ByoCostBreakdown | undefined;
  let totalCost = baseCost;

  if (revenueHistory && costPriceAnalysis.menuItemName && costPriceAnalysis.costPriceType !== COST_PRICE_TYPE_CHOICE) {
    const aggs = getByoChoiceAggregates(costPriceAnalysis.menuItemName, revenueHistory);
    if (aggs && aggs.choices.size > 0) {
      // Resolve every choice name to its CHOICE analysis (shared library, one per
      // restaurant + name). Missing CHOICE rows → perPickCost = 0 (decision 9).
      const choiceRows: ByoCostBreakdown['choices'] = [];
      let weightedChoiceCost = 0;
      let totalChoiceRevenue = 0;
      let totalChoiceRevenueExVat = 0;

      aggs.choices.forEach((choiceAgg, name) => {
        const choiceAnalysis = allCostPriceAnalyses?.find(
          a => a.costPriceType === COST_PRICE_TYPE_CHOICE
            && a.restaurantId === costPriceAnalysis.restaurantId
            && a.costPriceName === name
        );

        let perPickCost: number | undefined;
        let isCosted = false;
        if (choiceAnalysis) {
          // Per-dish quantity overrides: this dish may use different portions
          // for this choice's ingredients than the shared CHOICE defines.
          const dishOverrides = costPriceAnalysis.byoChoiceOverrides?.[name];
          const choiceForDish = dishOverrides
            ? { ...choiceAnalysis, ingredients: applyByoQuantityOverrides(choiceAnalysis.ingredients, dishOverrides) }
            : choiceAnalysis;
          // Enrich the CHOICE recursively — its ingredients may themselves be
          // subrecipes / nested cost prices. Independent recursion (separate
          // visitedIds path) since CHOICE is shared and not a parent-child tree.
          const enrichedChoice = enrichCostPriceRecursive(
            choiceForDish,
            orderHistory,
            opts,
            _depth + 1,
            newVisitedIds
          );
          perPickCost = enrichedChoice.totalCost;
          isCosted = perPickCost > 0;
        }

        // A custom menu price on the CHOICE wins over the POS-derived avg paid
        // price (same priority rule as getEffectiveMenuPrice) — covers add-ons
        // whose surcharge isn't recorded in the revenue data.
        const customChoicePrice = choiceAnalysis?.customMenuPrice;
        const unitPrice = customChoicePrice
          ? customChoicePrice
          : choiceAgg.qtySold > 0 ? choiceAgg.revenue / choiceAgg.qtySold : 0;
        const unitPriceExVat = customChoicePrice
          ? (choiceAnalysis?.customMenuPriceExVat || customChoicePrice / 1.21)
          : choiceAgg.qtySold > 0 ? choiceAgg.revenueExVat / choiceAgg.qtySold : 0;

        choiceRows.push({
          name,
          perPickCost,
          qtySold: choiceAgg.qtySold,
          unitPrice,
          unitPriceExVat,
          isCosted,
        });

        weightedChoiceCost += (perPickCost ?? 0) * choiceAgg.qtySold;
        totalChoiceRevenue += customChoicePrice ? unitPrice * choiceAgg.qtySold : choiceAgg.revenue;
        totalChoiceRevenueExVat += customChoicePrice ? unitPriceExVat * choiceAgg.qtySold : choiceAgg.revenueExVat;
      });

      // Window has matching revenue items but somehow baseQty=0 (defensive — fall back to base).
      if (aggs.baseQty > 0) {
        const weightedDishCost = baseCost * aggs.baseQty + weightedChoiceCost;
        const weightedAvgCostPerDish = weightedDishCost / aggs.baseQty;
        // A custom menu price on a BYO dish is its BASE price — choice
        // contributions stack on top. Both the weighted headline and the
        // composer derive from this same base, so toggling a category off can
        // only move the price down, never jump between price sources.
        const customBase = costPriceAnalysis.customMenuPrice;
        const baseItemPrice = customBase || aggs.baseRevenue / aggs.baseQty;
        const baseItemPriceExVat = customBase
          ? (costPriceAnalysis.customMenuPriceExVat || customBase / 1.21)
          : aggs.baseRevenueExVat / aggs.baseQty;
        const weightedMenuPrice = baseItemPrice + totalChoiceRevenue / aggs.baseQty;
        const weightedMenuPriceExVat = baseItemPriceExVat + totalChoiceRevenueExVat / aggs.baseQty;

        byo = {
          baseCost,
          baseQty: aggs.baseQty,
          choices: choiceRows.sort((a, b) => b.qtySold - a.qtySold),
          weightedDishCost,
          weightedAvgCostPerDish,
          weightedMenuPrice,
          weightedMenuPriceExVat,
          baseItemPrice,
          baseItemPriceExVat,
        };
        totalCost = weightedAvgCostPerDish;
      }
    }
  }

  return {
    ...costPriceAnalysis,
    ingredients: enrichedIngredients,
    totalCost,
    ...(byo ? { byo } : {}),
  };
}

/**
 * Enriches multiple cost price analyses with current pricing data.
 * BYO dishes get their weighted breakdown attached when `revenueHistory` is supplied;
 * omit it to preserve the pre-BYO behaviour for legacy callers.
 */
export function enrichCostPriceListWithCurrentPrices(
  costPriceList: CostPriceAnalysis[],
  orderHistory: OrderHistory[],
  opts: EnrichmentOptions = {}
): EnrichedCostPriceAnalysis[] {
  // Pass the entire list as `allCostPriceAnalyses` so nested items can reference
  // other analyses (overrides any caller-supplied value).
  return costPriceList.map(analysis =>
    enrichCostPriceWithCurrentPrices(analysis, orderHistory, {
      ...opts,
      allCostPriceAnalyses: costPriceList,
    })
  );
}

/**
 * Finds revenue items that match a cost price analysis menu item name
 */
export function findMatchingRevenueItems(
  menuItemName: string,
  revenueHistory: RevenueHistory[]
): Array<{ price: number; date: Date; source: string }> {
  const matches: Array<{ price: number; date: Date; source: string }> = [];
  
  revenueHistory.forEach(revenue => {
    if (revenue.revenueData && revenue.revenueData.categories) {
      revenue.revenueData.categories.forEach(category => {
        if (category.items) {
          category.items.forEach(item => {
            if (item.name && item.name.toLowerCase().includes(menuItemName.toLowerCase())) {
              matches.push({
                price: item.price || 0,
                date: new Date(revenue.date),
                source: revenue.revenueSource || 'Unknown'
              });
            }
          });
        }
      });
    }
  });
  
  return matches.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Gets the effective menu price for a cost price analysis.
 * Priority: 1) BYO weighted menu price 2) Custom price override 3) CHOICE avg unit price
 *           4) Revenue data 5) Custom item price (fallback while no revenue exists)
 *
 * Accepts `EnrichedCostPriceAnalysis` so BYO dishes can return their weighted
 * menu price (`byo.weightedMenuPrice`) — the average customer ticket including
 * choices — instead of the base-only `findLatestMenuPrice` value. On a BYO dish
 * a custom menu price acts as the BASE price and is folded into
 * `byo.weightedMenuPrice` by enrichment, so the BYO branch must win.
 *
 * CHOICE rows have no `menuItemName`, so they get an "avg unit price per pick"
 * derived from the choice's revenue across every BYO dish in the trailing
 * window. Without this, opening a CHOICE detail modal showed Menu Price = 0
 * even when the choice clearly added to ticket revenue.
 */
export function getEffectiveMenuPrice(
  analysis: EnrichedCostPriceAnalysis,
  revenueHistory: RevenueHistory[]
): { menuPrice: number; menuExVatPrice: number; date: Date; source: string; isCustom: boolean; isCustomItem?: boolean } | null {
  // Priority 1: BYO weighted menu price (full ticket = base + choices, averaged
  // across the weighting window). Only attached by `enrichCostPriceWithCurrentPrices`
  // when revenueHistory is supplied AND the dish has choices in the window.
  // Checked BEFORE the custom price: on a BYO dish a custom menu price is the
  // BASE price and enrichment already folds it into `byo.weightedMenuPrice` —
  // returning the bare custom price here would drop the choice revenue.
  if (analysis.byo) {
    return {
      menuPrice: analysis.byo.weightedMenuPrice,
      menuExVatPrice: analysis.byo.weightedMenuPriceExVat,
      date: new Date(analysis.updatedAt),
      source: 'BYO weighted',
      isCustom: !!analysis.customMenuPrice,
      isCustomItem: false,
    };
  }

  // Priority 2: Custom price override (for adjusting existing items)
  if (analysis.customMenuPrice) {
    return {
      menuPrice: analysis.customMenuPrice,
      menuExVatPrice: analysis.customMenuPriceExVat || analysis.customMenuPrice / 1.21,
      date: new Date(analysis.updatedAt),
      source: 'Custom Price',
      isCustom: true,
      isCustomItem: false
    };
  }

  // Priority 3: CHOICE rows — avg unit price the customer paid per pick of this
  // choice, aggregated across every BYO dish in the trailing window.
  if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) {
    const choiceAgg = getChoiceAvgUnitPrice(analysis.costPriceName, revenueHistory);
    if (choiceAgg) {
      return {
        menuPrice: choiceAgg.unitPrice,
        menuExVatPrice: choiceAgg.unitPriceExVat,
        date: new Date(analysis.updatedAt),
        source: 'Choice avg',
        isCustom: false,
        isCustomItem: false,
      };
    }
    return null;
  }

  // Priority 4: Revenue data. Also applies to custom items that have since been
  // linked to a real revenue item — real POS data beats the planned price.
  if (analysis.menuItemName) {
    const latestPrice = findLatestMenuPrice(analysis.menuItemName, revenueHistory);
    if (latestPrice) {
      return { ...latestPrice, isCustom: false, isCustomItem: false };
    }
  }

  // Priority 5: Custom item price — the planned price of a custom item, used
  // only while no revenue data exists for it yet.
  if (analysis.isCustomItem && analysis.customItemPrice) {
    return {
      menuPrice: analysis.customItemPrice,
      menuExVatPrice: analysis.customItemPriceExVat || analysis.customItemPrice / 1.21,
      date: new Date(analysis.createdAt),
      source: 'Custom Item',
      isCustom: false,
      isCustomItem: true
    };
  }

  return null;
}

type MenuPriceResult = { menuPrice: number; menuExVatPrice: number; date: Date; source: string } | null;

// Revenue entries are daily aggregates, so a comped or discounted portion dilutes that day's
// price/quantity ("gave 2 away" → unit price drops for one day, fully comped → 0). The menu price
// is therefore resolved over the item's trailing window instead of just its single latest entry.
const MENU_PRICE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

type MenuPriceOccurrence = { unitPrice: number; unitExVatPrice: number; date: Date; source: string };

// Resolves an item's menu price from its window of occurrences (date-descending): the most common
// cent-rounded unit price wins; on a tie — including when every price occurs once — the most
// recent one wins. Zero-priced entries (comped days, zero quantities) never win unless the window
// holds nothing else.
function resolveMenuPrice(occurrences: MenuPriceOccurrence[]): MenuPriceResult {
  const toResult = (o: MenuPriceOccurrence): MenuPriceResult => ({
    menuPrice: o.unitPrice,
    menuExVatPrice: o.unitExVatPrice,
    date: o.date,
    source: o.source,
  });

  const groups = new Map<number, { count: number; latest: MenuPriceOccurrence }>();
  for (const o of occurrences) {
    if (o.unitPrice <= 0) continue;
    const key = Math.round(o.unitPrice * 100);
    const group = groups.get(key);
    if (group) group.count++;
    else groups.set(key, { count: 1, latest: o });
  }
  // Every entry in the window was zero — surface the latest one as-is (menu price 0).
  if (groups.size === 0) return toResult(occurrences[0]);

  // Insertion order = recency of each group's latest entry, so strict > keeps the most recent
  // group on a tie.
  let best: { count: number; latest: MenuPriceOccurrence } | undefined;
  groups.forEach(group => {
    if (!best || group.count > best.count) best = group;
  });
  return toResult(best!.latest);
}

// Per-revenue-array caches for findLatestMenuPrice, keyed on the array identity so each period's
// revenue slice caches independently and a new array (data arrived) starts fresh.
//   menuPriceIndexCache — every DISTINCT item name in the array, in scan order (revenue sorted
//     date-desc, then category order, then item order, first occurrence deciding the position),
//     each resolved via resolveMenuPrice over the item's own trailing window. Built in one pass
//     instead of re-walking every revenue record x category x item for each dish.
//     Keyed on the EXACT name (with the lowercased form kept alongside for the fallbacks): a
//     partner venue sells the restaurant's own dishes under the same name in different casing
//     at its own prices, and merging those occurrences would give both rows one blended price.
//   menuPriceCache — the per-menu-item answer, so a repeated lookup skips even the index scan.
const menuPriceIndexCache = new WeakMap<RevenueHistory[], Array<{ name: string; nameLower: string; result: MenuPriceResult }>>();
const menuPriceCache = new WeakMap<RevenueHistory[], Map<string, MenuPriceResult>>();

function getMenuPriceIndex(revenueHistory: RevenueHistory[]): Array<{ name: string; nameLower: string; result: MenuPriceResult }> {
  const cached = menuPriceIndexCache.get(revenueHistory);
  if (cached) return cached;

  // Date-descending so each item's first occurrence is its latest entry (Array#sort is stable, so
  // revenue entries sharing a date keep their original relative order).
  const sortedRevenue = [...revenueHistory].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const byName = new Map<string, MenuPriceOccurrence[]>();
  for (const revenue of sortedRevenue) {
    if (revenue.revenueData && revenue.revenueData.categories) {
      for (const category of revenue.revenueData.categories) {
        if (category.items) {
          for (const item of category.items) {
            if (!item.name) continue;
            let occurrences = byName.get(item.name);
            if (!occurrences) {
              occurrences = [];
              byName.set(item.name, occurrences);
            }
            const date = new Date(revenue.date);
            // The item's own latest entry anchors its window (not "today"), so an item that
            // stopped selling weeks ago still resolves to its last known price.
            if (occurrences.length > 0 && occurrences[0].date.getTime() - date.getTime() > MENU_PRICE_WINDOW_MS) continue;
            occurrences.push({
              // Unit price as price / quantity (including VAT), ex-VAT as exVat / quantity.
              unitPrice: item.quantity > 0 ? item.price / item.quantity : 0,
              unitExVatPrice: item.quantity > 0 ? item.exVat / item.quantity : 0,
              date,
              source: revenue.revenueSource || 'Unknown',
            });
          }
        }
      }
    }
  }

  const ordered: Array<{ name: string; nameLower: string; result: MenuPriceResult }> = [];
  byName.forEach((occurrences, name) => {
    ordered.push({ name, nameLower: name.toLowerCase(), result: resolveMenuPrice(occurrences) });
  });
  menuPriceIndexCache.set(revenueHistory, ordered);
  return ordered;
}

/**
 * Finds the current menu price for a specific menu item from revenue data: the most common unit
 * price over the item's last 14 days of entries (latest non-zero on ties / all-unique — see
 * resolveMenuPrice).
 */
export function findLatestMenuPrice(
  menuItemName: string,
  revenueHistory: RevenueHistory[]
): MenuPriceResult {
  // The exact name decides the result (an exact-case entry wins over its other-cased twin).
  const cacheKey = menuItemName;
  let resultsForArray = menuPriceCache.get(revenueHistory);
  if (!resultsForArray) {
    resultsForArray = new Map<string, MenuPriceResult>();
    menuPriceCache.set(revenueHistory, resultsForArray);
  }

  let result: MenuPriceResult;
  if (resultsForArray.has(cacheKey)) {
    result = resultsForArray.get(cacheKey)!;
  } else {
    result = findLatestMenuPriceUncached(menuItemName, revenueHistory);
    resultsForArray.set(cacheKey, result);
  }

  // Always hand back a copy, exactly like the old per-call object, so a caller mutating the result
  // can't poison the cache.
  return result ? { ...result } : null;
}

function findLatestMenuPriceUncached(
  menuItemName: string,
  revenueHistory: RevenueHistory[]
): MenuPriceResult {
  const query = menuItemName.toLowerCase();
  // Exact name match wins: a substring match can hit a different, cheaper product whose name merely
  // contains the dish name ("Brioche Steak Tartare" vs "Steak Tartare"). Only when no exact entry
  // exists anywhere in the index do we fall back to the first name-containing entry in scan order
  // ("cola" can still resolve to "cola light").
  //
  // Exact CASE wins over an exact-but-differently-cased name, so a partner venue's rows resolve to
  // that venue's own prices instead of blending with the restaurant's identically-named dish. The
  // case-insensitive step stays as the fallback: a link whose casing drifted from the revenue data
  // must keep resolving exactly as it did before.
  let caseInsensitive: MenuPriceResult = null;
  let partial: MenuPriceResult = null;
  for (const entry of getMenuPriceIndex(revenueHistory)) {
    if (entry.name === menuItemName) return entry.result;
    if (!caseInsensitive && entry.nameLower === query) caseInsensitive = entry.result;
    if (!partial && entry.nameLower.includes(query)) partial = entry.result;
  }

  return caseInsensitive ?? partial;
}

/**
 * Calculates profit margin based on cost and menu price
 */
export function calculateMargin(totalCost: number, menuPrice: number): number {
  if (menuPrice <= 0) return 0;
  return ((menuPrice - totalCost) / menuPrice) * 100;
}

/**
 * Calculates cost percentage based on cost and menu price
 */
export function calculateCostPercentage(totalCost: number, menuPrice: number): number {
  if (menuPrice <= 0) return 0;
  return (totalCost / menuPrice) * 100;
}

/**
 * A dish counts as "analyzed" once it has at least one ingredient — NOT when its cost
 * is > 0, so dishes made of free ingredients (e.g. tea bags at €0) still count. Same
 * "has ingredients" rule as the setup score's isCostLinked
 * (server/database/setupScoreDbService.ts); keep the two in sync.
 */
export function isAnalyzedDish(analysis: CostPriceAnalysis): boolean {
  return (analysis.ingredients?.length ?? 0) > 0;
}

/**
 * Category statistics interface
 */
export interface CategoryStats {
  totalItems: number;
  analyzedItems: number;
  avgCostPercentage: number;
  weightedCostPercentage: number;
}

/**
 * Calculate category statistics (total items, analyzed items, cost percentages)
 */
export const calculateCategoryStats = (
  items: CostPriceAnalysis[],
  revenueHistory: RevenueHistory[]
): CategoryStats => {
  const totalItems = items.length;
  const analyzedItems = items.filter(isAnalyzedDish).length;

  // Calculate cost percentages only for items with both cost and price data
  const itemsWithData = items.filter(item => {
    if (!item.totalCost || item.totalCost <= 0) return false;
    const effectivePrice = getEffectiveMenuPrice(item, revenueHistory);
    return effectivePrice !== null && effectivePrice.menuPrice > 0;
  });

  if (itemsWithData.length === 0) {
    return {
      totalItems,
      analyzedItems,
      avgCostPercentage: 0,
      weightedCostPercentage: 0,
    };
  }

  // Calculate average cost percentage (non-weighted)
  const costPercentages = itemsWithData.map(item => {
    const effectivePrice = getEffectiveMenuPrice(item, revenueHistory);
    if (!effectivePrice || !item.totalCost) return 0;
    return calculateCostPercentage(item.totalCost, effectivePrice.menuPrice);
  });

  const avgCostPercentage = costPercentages.reduce((sum, pct) => sum + pct, 0) / costPercentages.length;

  // Aggregate theoretical costs / revenue at current menu prices, using the
  // last 2 months' sales mix. Weighting percentages by units alone overweights
  // cheaper dishes relative to their contribution to revenue.
  const itemsWithQuantity = itemsWithData.map(item => {
    const effectivePrice = getEffectiveMenuPrice(item, revenueHistory);
    if (!effectivePrice || !item.totalCost) return { cost: 0, revenue: 0 };

    const quantity = item.menuItemName ? getQuantitySoldLast14Days(item.menuItemName, revenueHistory) : 0;

    return { cost: item.totalCost * quantity, revenue: effectivePrice.menuPrice * quantity };
  });

  const totalRevenue = itemsWithQuantity.reduce((sum, item) => sum + item.revenue, 0);
  const totalCost = itemsWithQuantity.reduce((sum, item) => sum + item.cost, 0);
  const weightedCostPercentage = totalRevenue > 0
    ? calculateCostPercentage(totalCost, totalRevenue)
    : avgCostPercentage;

  return {
    totalItems,
    analyzedItems,
    avgCostPercentage,
    weightedCostPercentage,
  };
};

// BYO = build-your-own.
// Same trailing 2-month window as `getQuantitySoldLast14Days` so BYO weighting
// stays in lockstep with the popularity / KPI windows the rest of cost price uses.
const BYO_WINDOW_MONTHS = 2;

export interface ByoChoiceAggregates {
  baseQty: number;
  baseRevenue: number;
  baseRevenueExVat: number;
  choices: Map<string, { qtySold: number; revenue: number; revenueExVat: number }>;
}

// One aggregate per menu item name, built in a SINGLE pass over a revenue array instead of one full
// pass per dish (the charts page asked for this ~700 dishes x 12 periods deep). Keyed on the array
// identity; the trailing-window cutoff is evaluated once when the index is built rather than per call,
// which cannot change a result — the window boundary is date-granular and revenue is dated per day.
// Treat the returned aggregate as READ-ONLY: it is shared between callers.
const byoAggregatesCache = new WeakMap<RevenueHistory[], Map<string, ByoChoiceAggregates>>();

function getByoAggregatesIndex(revenueHistory: RevenueHistory[]): Map<string, ByoChoiceAggregates> {
  const cached = byoAggregatesCache.get(revenueHistory);
  if (cached) return cached;

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - BYO_WINDOW_MONTHS);

  const index = new Map<string, ByoChoiceAggregates>();
  for (const revenue of revenueHistory) {
    if (new Date(revenue.date) < cutoff) continue;
    for (const category of revenue.revenueData.categories) {
      for (const item of category.items) {
        // A falsy item name can never equal the (guarded non-empty) menuItemName it's matched against.
        if (!item.name) continue;
        let agg = index.get(item.name);
        if (!agg) {
          agg = { baseQty: 0, baseRevenue: 0, baseRevenueExVat: 0, choices: new Map() };
          index.set(item.name, agg);
        }
        agg.baseQty += item.quantity || 0;
        agg.baseRevenue += item.price || 0;
        agg.baseRevenueExVat += item.exVat || 0;
        for (const choice of item.choices ?? []) {
          const existing = agg.choices.get(choice.name);
          if (existing) {
            existing.qtySold += choice.quantity || 0;
            existing.revenue += choice.price || 0;
            existing.revenueExVat += choice.exVat || 0;
          } else {
            agg.choices.set(choice.name, {
              qtySold: choice.quantity || 0,
              revenue: choice.price || 0,
              revenueExVat: choice.exVat || 0,
            });
          }
        }
      }
    }
  }

  byoAggregatesCache.set(revenueHistory, index);
  return index;
}

/**
 * Per-choice aggregates for a BYO dish over the trailing window. Reads the dish's
 * linked revenue items (matched by `menuItemName`) out of the shared index above:
 * `baseQty`, `baseRevenue` / `baseRevenueExVat`, and per-exact-choice `qtySold` /
 * `revenue` / `revenueExVat`. Choice names are NOT normalised — `kipfilet` and
 * `kipfilet Extra` stay distinct (decision 2).
 *
 * Returns `null` when `menuItemName` is missing or no matching revenue items
 * fall inside the window — in that case the dish is not BYO.
 */
export function getByoChoiceAggregates(
  menuItemName: string | undefined,
  revenueHistory: RevenueHistory[]
): ByoChoiceAggregates | null {
  if (!menuItemName) return null;
  return getByoAggregatesIndex(revenueHistory).get(menuItemName) ?? null;
}

/**
 * Avg unit price the customer pays per pick of a given choice, across every BYO
 * dish in the trailing 2-month window. The CHOICE analysis itself has no
 * `menuItemName`, so its "menu price" is derived from what customers actually
 * paid for it wherever it appeared. Used by `getEffectiveMenuPrice` so a CHOICE
 * detail modal doesn't show €0.00 menu price when there is real ticket data.
 */
export function getChoiceAvgUnitPrice(
  choiceName: string,
  revenueHistory: RevenueHistory[],
): { qtySold: number; unitPrice: number; unitPriceExVat: number } | null {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - BYO_WINDOW_MONTHS);

  let qtySold = 0;
  let revenue = 0;
  let revenueExVat = 0;
  let sawAny = false;

  for (const rh of revenueHistory) {
    if (new Date(rh.date) < cutoff) continue;
    for (const category of rh.revenueData.categories) {
      for (const item of category.items) {
        for (const choice of item.choices ?? []) {
          if (choice.name !== choiceName) continue;
          sawAny = true;
          qtySold += choice.quantity || 0;
          revenue += choice.price || 0;
          revenueExVat += choice.exVat || 0;
        }
      }
    }
  }

  if (!sawAny || qtySold <= 0) return null;
  return {
    qtySold,
    unitPrice: revenue / qtySold,
    unitPriceExVat: revenueExVat / qtySold,
  };
}

/**
 * BYO = build-your-own.
 * A `CostPriceAnalysis` is BYO iff its linked revenue item (by `menuItemName`)
 * has at least one choice somewhere in the trailing 2-month window.
 * No flag is stored — this is purely a derivation from revenue data at read time.
 */
export function isByoDish(analysis: CostPriceAnalysis, revenueHistory: RevenueHistory[]): boolean {
  const aggs = getByoChoiceAggregates(analysis.menuItemName, revenueHistory);
  return !!aggs && aggs.choices.size > 0;
}

/**
 * Helper function to calculate quantity sold in last 2 months for a revenue item
 */
export function getQuantitySoldLast14Days(
  itemName: string,
  revenueHistory: RevenueHistory[]
): number {
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

  return revenueHistory
    .filter(revenue => new Date(revenue.date) >= twoMonthsAgo)
    .reduce((total, revenue) => {
      const itemQuantity = revenue.revenueData.categories.reduce((catTotal, category) => {
        const item = category.items.find(i => i.name === itemName);
        return catTotal + (item?.quantity || 0);
      }, 0);
      return total + itemQuantity;
    }, 0);
}

/**
 * Currency formatter for EUR
 */
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format number as EUR currency
 */
export const formatEur = (n?: number) => currencyFormatter.format(n ?? 0);

/**
 * Gets the menu category name for a given item from revenue history
 * Looks through revenue data to find which POS category the item belongs to
 */
export function getMenuCategoryForItem(itemName: string, revenueHistory: RevenueHistory[]): string | null {
  for (const revenue of revenueHistory) {
    for (const category of revenue.revenueData.categories) {
      const item = category.items.find(i => i.name === itemName);
      if (item) {
        return category.name;
      }
    }
  }
  return null;
}

/**
 * Revenue item data for calculations
 */
export interface RevenueItemData {
  totalRevenue: number;
  quantity: number;
  avgPrice: number;
}

/**
 * Build a map of revenue items with aggregated data (total revenue, quantity, avg price)
 */
export function buildRevenueItemsMap(
  revenueHistory: RevenueHistory[],
  showExVat: boolean = true
): Map<string, RevenueItemData> {
  const revenueItemsMap = new Map<string, RevenueItemData>();

  revenueHistory.forEach(revenue => {
    revenue.revenueData.categories.forEach(category => {
      category.items.forEach(item => {
        const key = item.name;
        // Total ticket revenue = base + nested choices. For a BYO dish this is the
        // full price the customer paid, so `avgPrice` here is the realistic
        // "what does a wok actually sell for" figure.
        const itemRevenue = getItemTotalRevenue(item, showExVat);

        if (!revenueItemsMap.has(key)) {
          revenueItemsMap.set(key, {
            totalRevenue: itemRevenue,
            quantity: item.quantity,
            avgPrice: item.quantity > 0 ? itemRevenue / item.quantity : 0
          });
        } else {
          const existing = revenueItemsMap.get(key)!;
          existing.totalRevenue += itemRevenue;
          existing.quantity += item.quantity;
          existing.avgPrice = existing.quantity > 0 ? existing.totalRevenue / existing.quantity : 0;
        }
      });
    });
  });

  return revenueItemsMap;
}

/**
 * Get the selling price for a cost price analysis item
 */
export function getSellingPrice(
  analysis: CostPriceAnalysis,
  revenueItemsMap: Map<string, RevenueItemData>,
  showExVat: boolean = true
): number {
  // Get selling price from custom price or revenue data
  if (analysis.customMenuPrice !== undefined && analysis.customMenuPrice !== null) {
    // Use custom menu price if set
    return showExVat
      ? (analysis.customMenuPriceExVat || 0)
      : (analysis.customMenuPrice || 0);
  }

  // Revenue data — also for custom items that have been linked to a revenue item
  if (analysis.menuItemName) {
    const revenueData = revenueItemsMap.get(analysis.menuItemName);
    if (revenueData) {
      return revenueData.avgPrice;
    }
  }

  // Custom item planned price — only while no revenue data exists for it
  if (analysis.isCustomItem) {
    return showExVat
      ? (analysis.customItemPriceExVat || 0)
      : (analysis.customItemPrice || 0);
  }

  return 0;
}

/**
 * KPI Metrics interface
 */
export interface CostPriceKPIMetrics {
  avgCostPrice: number;
  weightedCostPrice: number;
  analyzedCount: number;
  totalCount: number;
}

export function calculateCostPriceKPIs(
  enrichedCostPriceList: CostPriceAnalysis[],
  revenueHistory: RevenueHistory[],
  revenueItems: string[],
  showExVat: boolean = true,
  // When a menu filter is active (specific menus or "active dishes"), the
  // "Total Analyzed X/Y" denominator should be exactly the filtered revenue
  // items — not revenue items + every manually-created custom item. Custom
  // (non-POS) items are only added to the total in the unfiltered "All" view.
  scopeToRevenueItems: boolean = false
): CostPriceKPIMetrics {
  // Only include real analyses (not placeholders, not CHOICE rows — those are
  // shared per-pick costs that don't represent a sold menu item, and not
  // marked-irrelevant dishes — those are "n/a" and leave every X/Y count,
  // matching the setup-score denominator semantics).
  const validAnalyses = enrichedCostPriceList.filter(
    analysis => !analysis.id.startsWith('placeholder-')
      && analysis.costPriceType !== COST_PRICE_TYPE_CHOICE
      && !analysis.markedIrrelevant
  );

  // Items with both cost AND valid menu price
  const itemsWithData = validAnalyses.filter(analysis => {
    if (!analysis.totalCost || analysis.totalCost <= 0) return false;

    const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory);
    if (!effectivePrice) return false;

    const menuPrice = showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
    return menuPrice > 0;
  });

  // Calculate average (simple)
  const costPercentages = itemsWithData.map(analysis => {
    const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory)!;
    const menuPrice = showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
    return calculateCostPercentage(analysis.totalCost, menuPrice);
  });

  const avgCostPrice = costPercentages.length > 0
    ? costPercentages.reduce((a, b) => a + b, 0) / costPercentages.length
    : 0;

  // Aggregate theoretical costs / revenue at current menu prices, using the
  // last 2 months' sales mix and the same VAT basis as the enriched costs.
  const weightedItems = itemsWithData.map(analysis => {
    const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory)!;
    const menuPrice = showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
    const quantity = analysis.menuItemName ? getQuantitySoldLast14Days(analysis.menuItemName, revenueHistory) : 0;

    return { cost: analysis.totalCost * quantity, revenue: menuPrice * quantity };
  });

  const totalRevenue = weightedItems.reduce((sum, item) => sum + item.revenue, 0);
  const totalCost = weightedItems.reduce((sum, item) => sum + item.cost, 0);

  const weightedCostPrice = totalRevenue > 0
    ? calculateCostPercentage(totalCost, totalRevenue)
    : avgCostPrice; // fallback

  // Counts
  const revenueItemSet = new Set(revenueItems);

  let analyzedCount: number;
  let totalCount: number;
  if (scopeToRevenueItems) {
    // Filtered view (specific menus / active dishes): count EVERY real dish in the
    // filtered set — revenue-linked AND custom (manually-created) dishes — so the
    // X/Y matches the dishes the user actually sees in the list for that menu.
    // `validAnalyses` already excludes placeholders and CHOICE rows.
    analyzedCount = validAnalyses.filter(isAnalyzedDish).length;
    totalCount = validAnalyses.length;
  } else {
    // Unfiltered ("All") view: every analyzed analysis, over every revenue item
    // plus custom items that aren't backed by a revenue item. (Custom items with a
    // menuItemName matching a revenue item are not double-counted.)
    analyzedCount = validAnalyses.filter(isAnalyzedDish).length;
    const customItemsNotInRevenue = validAnalyses.filter(
      a => a.isCustomItem && (!a.menuItemName || !revenueItemSet.has(a.menuItemName))
    );
    // Revenue items whose analysis is marked irrelevant also leave the denominator
    // (the marked row was created from the revenue item, so the names match exactly).
    const markedIrrelevantNames = new Set(
      enrichedCostPriceList
        .filter(a => a.markedIrrelevant && a.menuItemName)
        .map(a => a.menuItemName as string)
    );
    const relevantRevenueItems = revenueItems.filter(name => !markedIrrelevantNames.has(name));
    totalCount = relevantRevenueItems.length + customItemsNotInRevenue.length;
  }

  return {
    avgCostPrice: Number(avgCostPrice.toFixed(1)),
    weightedCostPrice: Number(weightedCostPrice.toFixed(1)),
    analyzedCount,
    totalCount
  };
}

/**
 * Creates a cost price analysis for a placeholder item (revenue item without cost price data)
 * This is a reusable utility for automatically creating cost price items when clicking on placeholders
 */
export async function createCostPriceFromPlaceholder(
  itemData: {
    costPriceName: string;
    menuItemName?: string;
    restaurantId: string;
  },
  categoriesStore: {
    reload: () => Promise<void>;
    getDefaultCategory: () => { id: string } | undefined;
    categories: Array<{ id: string }>;
  },
  costPriceStore: {
    load: (restaurantId: string) => Promise<void>;
  },
  createCostPriceAnalysis: (request: any) => Promise<CostPriceAnalysis>
): Promise<CostPriceAnalysis> {
  const { costPriceName, menuItemName, restaurantId } = itemData;

  // Reload categories to ensure we have the latest data
  await categoriesStore.reload();

  // Try to find a default category, or use any existing category
  let categoryId: string | undefined;
  const defaultCategory = categoriesStore.getDefaultCategory();

  if (defaultCategory) {
    categoryId = defaultCategory.id;
  } else if (categoriesStore.categories.length > 0) {
    // Use the first available category if no default exists
    categoryId = categoriesStore.categories[0].id;
  }
  // If no categories exist, categoryId will be undefined and backend will handle it

  const createRequest = {
    restaurantId,
    costPriceName,
    menuItemName,
    categoryId,
    ingredients: []
  };

  const newAnalysis = await createCostPriceAnalysis(createRequest);
  await costPriceStore.load(restaurantId);

  return newAnalysis;
}

/**
 * Item with highest margin data
 */
export interface HighestMarginItem {
  itemName: string;
  margin: number;
}

/**
 * Gets the item with the highest margin amount (price - cost)
 * Returns null if no valid items found
 */
export function getItemWithHighestMargin(
  enrichedCostPriceList: CostPriceAnalysis[],
  revenueHistory: RevenueHistory[],
  showExVat: boolean = true
): HighestMarginItem | null {
  // Only include items with both cost and price data
  const validItems = enrichedCostPriceList
    .filter(analysis => {
      // Skip placeholders + CHOICE rows (no menu price by design).
      if (analysis.id.startsWith('placeholder-')) return false;
      if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) return false;

      // Must have cost data
      if (!analysis.totalCost || analysis.totalCost <= 0) return false;

      // Must have valid menu price
      const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory);
      if (!effectivePrice) return false;

      const menuPrice = showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
      return menuPrice > 0;
    })
    .map(analysis => {
      const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory)!;
      const menuPrice = showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
      const margin = menuPrice - analysis.totalCost;

      return {
        itemName: analysis.costPriceName,
        margin
      };
    })
    .sort((a, b) => b.margin - a.margin); // Sort descending by margin

  // Return the first item (highest margin), or null if no items
  return validItems.length > 0 ? validItems[0] : null;
}

/**
 * Most sold item data
 */
export interface MostSoldItem {
  itemName: string;
  quantitySold: number;
}

/**
 * Gets the most sold item based on quantity sold in last 2 months
 * Returns null if no valid items found
 */
export function getMostSoldItem(
  enrichedCostPriceList: CostPriceAnalysis[],
  revenueHistory: RevenueHistory[],
  revenueItems: string[]
): MostSoldItem | null {
  // Build a map of all revenue items with their quantities
  const itemQuantities = new Map<string, number>();

  // Add all revenue items with their quantities
  revenueItems.forEach(itemName => {
    const quantity = getQuantitySoldLast14Days(itemName, revenueHistory);
    if (quantity > 0) {
      itemQuantities.set(itemName, quantity);
    }
  });

  // Also check custom items that might be linked to revenue (exclude CHOICE rows).
  enrichedCostPriceList.forEach(analysis => {
    if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) return;
    if (analysis.menuItemName && !analysis.id.startsWith('placeholder-')) {
      const quantity = getQuantitySoldLast14Days(analysis.menuItemName, revenueHistory);
      if (quantity > 0) {
        // Use the cost price name for display
        itemQuantities.set(analysis.costPriceName, quantity);
      }
    }
  });

  if (itemQuantities.size === 0) return null;

  // Find the item with highest quantity
  let mostSold: MostSoldItem | null = null;
  let maxQuantity = 0;

  itemQuantities.forEach((quantity, itemName) => {
    if (quantity > maxQuantity) {
      maxQuantity = quantity;
      mostSold = { itemName, quantitySold: quantity };
    }
  });

  return mostSold;
}
